import { Component, OnInit, Input, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { EnvService } from '@core-mkt/services/env/env.service';
import { Image } from '@core-mkt/interfaces/image';
import { canonicalIdBase } from '@core-mkt/services/schema/schema-id.util';
import { getBrandSiteUrl } from '@core-mkt/services/schema/organization.schema';
import type { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { Reviews } from '../reviews';
import { ComponentTheme, ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { CardReviewsComponent } from '../card-reviews/card-reviews.component';
import { getCardReviewsSchema } from '../card-reviews/card-reviews.schema';
import { CarouselReviewsComponent } from '../carousel-reviews/carousel-reviews.component';
import { getCarouselReviewsSchema } from '../carousel-reviews/carousel-reviews.schema';
import { ColumnReviewsComponent } from '../column-reviews/column-reviews.component';
import { getColumnReviewsSchema } from '../column-reviews/column-reviews.schema';
import { ColumnType2ReviewsComponent } from '../column-type2-reviews/column-type2-reviews.component';
import { getColumnType2ReviewsSchema } from '../column-type2-reviews/column-type2-reviews.schema';
import { StarReviewsComponent } from '../star-reviews/star-reviews.component';
import { getStarReviewsSchema } from '../star-reviews/star-reviews.schema';
import { SingleReviewComponent } from '../single-review/single-review.component';
import { getSingleReviewSchema } from '../single-review/single-review.schema';

@Component({
  selector: 'reviews',
  templateUrl: './reviews.component.html',
  styleUrls: [],
  standalone: true,
  imports: [
    CardReviewsComponent,
    CarouselReviewsComponent,
    ColumnReviewsComponent,
    ColumnType2ReviewsComponent,
    StarReviewsComponent,
    SingleReviewComponent,
  ],
})
export class ReviewsComponent implements OnInit {
  @Input() data: Reviews;
  textColorOverride = '';
  textColorValue = '';
  backgroundColor = '';
  backgroundColorValue = '';
  containerClasses = '';

  private redactor = inject(WysiwygRedactorService);
  private themeParser = inject(ThemeParserService);
  private schemaService = inject(SchemaService);
  private env = inject(EnvService);
  private document = inject<Document>(DOCUMENT);

  get reviewType(): string {
    return this.data.reviewType;
  }

  get reviewTitle(): string {
    return this.data.reviewTitle;
  }

  get backgroundImage(): Image {
    return this.data.backgroundImage?.[0];
  }

  get textColor(): string {
    return this.data.textColor;
  }

  ngOnInit(): void {
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor,
      backgroundImage: this.data.backgroundImage?.[0],
      textColor: this.data.textColor,
      columnDistribution: undefined,
    };
    const theme = this.themeParser.getThemeClasses(themeInput);
    // Capture the raw review title before bypassSanitizer wraps it as SafeValue —
    // the schema function needs a plain string for ItemList.name.
    const rawReviewTitle = typeof this.data.reviewTitle === 'string' ? this.data.reviewTitle : undefined;
    this.data.reviewTitle = this.redactor.bypassSanitizer(this.data.reviewTitle);
    this.textColorOverride = this.data.textColor;
    if (this.data.styles && this.data.styles !== null) {
      this.backgroundColor = theme.root;
      this.textColorOverride = this.themeParser.getLastClassForType('text-', this.data.styles);
      this.containerClasses = theme.container;
    } else {
      this.backgroundColor = this.data.backgroundColor;
    }

    this.schemaService.register(this.buildSchemaForActiveVariant(rawReviewTitle));
  }

  /**
   * Resolves the active review variant from `reviewType` and returns its schema fragment.
   * Returns null when the variant is unrecognized so the SchemaService can ignore it.
   */
  private buildSchemaForActiveVariant(reviewTitle?: string): SchemaOrgNode | SchemaOrgNode[] | null {
    // Canonical (origin + pathname, no query/hash) so review/ItemList @ids don't
    // fragment across coupon/UTM/pagination variants of the same page.
    const pageUrl = canonicalIdBase(this.document.URL);
    // Canonical brand site root for the Organization @id — derived from
    // brandConfig.url so #organization is stable across environments (sandbox /
    // review-app hosts) and itemReviewed references resolve. Fall back to parsing
    // the runtime URL only when brand config has no url. See organization.schema.ts.
    const brandUrl = this.env.get.brandConfig?.url;
    const siteUrl = brandUrl ? getBrandSiteUrl(brandUrl) : this.extractSiteUrl(pageUrl);
    const organizationName = this.env.get.brandConfig.name ?? '';
    if (!organizationName) return null;

    const variantData = { pageUrl, siteUrl, organizationName, reviewTitle };

    switch (this.data.reviewType) {
      case 'cards':
        return getCardReviewsSchema(variantData);
      case 'slider':
        return getCarouselReviewsSchema(variantData);
      case 'columns':
        return getColumnReviewsSchema(variantData);
      case 'columnsType2':
        return getColumnType2ReviewsSchema(variantData);
      case 'stars':
        return getStarReviewsSchema(variantData);
      case 'single':
        return getSingleReviewSchema({ pageUrl, siteUrl, organizationName });
      default:
        return null;
    }
  }

  private extractSiteUrl(pageUrl: string): string {
    try {
      const parsed = new URL(pageUrl);
      return `${parsed.protocol}//${parsed.host}/`;
    } catch {
      return pageUrl;
    }
  }
}
